import { useEffect, useRef, useState } from "react";
import { PenLine, Eraser, Check, X, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { fileUrlToFull } from "@/components/ImageUpload";
import { toast } from "sonner";

/**
 * Signature field: collects { name, image_url } using a canvas drawing pad.
 */
export default function SignaturePad({ value, onChange, testid = "signature-pad" }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(value?.name || "");
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const lastRef = useRef({ x: 0, y: 0 });

  const ensureCtx = () => {
    const c = canvasRef.current;
    if (!c) return null;
    if (ctxRef.current) return ctxRef.current;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    if (!ctx) return null;
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "hsl(164 100% 18%)";
    ctxRef.current = ctx;
    return ctx;
  };

  useEffect(() => {
    if (!open) {
      ctxRef.current = null;
      setHasInk(false);
      return;
    }
    // Defer to next frame to ensure canvas is mounted in DOM
    const id = requestAnimationFrame(() => {
      ensureCtx();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = ensureCtx();
    if (!ctx) return;
    setDrawing(true);
    const p = pos(e);
    lastRef.current = p;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    if (!ctx) return;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    setHasInk(true);
  };
  const stop = () => setDrawing(false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = ctxRef.current;
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const save = async () => {
    if (!hasInk) {
      toast.error("الرجاء رسم التوقيع أولاً");
      return;
    }
    if (!name.trim()) {
      toast.error("الرجاء إدخال الاسم");
      return;
    }
    const c = canvasRef.current;
    if (!c) {
      toast.error("تعذّر التقاط التوقيع، أعد المحاولة");
      return;
    }
    setSaving(true);
    try {
      const blob = await new Promise((res) => c.toBlob(res, "image/png"));
      if (!blob) throw new Error("toBlob returned null");
      const fd = new FormData();
      fd.append("file", new File([blob], `signature-${Date.now()}.png`, { type: "image/png" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.({ name: name.trim(), url: data.url, type: "image/png" });
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={testid}>
      {value?.url ? (
        <div className="space-y-2">
          <div className="flex items-center gap-3 p-3 rounded-sm border border-border bg-surface">
            <img
              src={fileUrlToFull(value.url)}
              alt={value.name}
              className="h-16 bg-white border border-border rounded-sm object-contain"
            />
            <div className="flex-1">
              <div className="text-xs text-muted-foreground">التوقيع باسم</div>
              <div className="font-medium">{value.name}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => { setName(value.name || ""); setOpen(true); }}>
              <PenLine className="w-3.5 h-3.5 ms-1" /> إعادة التوقيع
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => onChange?.(null)} className="text-destructive">
              إزالة
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => { setName(""); setOpen(true); }}
          data-testid={`${testid}-button`}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-sm border-2 border-dashed border-border bg-card hover:border-primary hover:bg-accent transition-all text-sm text-muted-foreground hover:text-primary"
        >
          <PenLine className="w-4 h-4" />
          اضغط للتوقيع
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-xl" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <PenLine className="w-5 h-5 text-primary" /> توقيع إلكتروني
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">الاسم الكامل للموقّع</Label>
              <Input
                data-testid={`${testid}-name`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="أدخل اسمك"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">ارسم توقيعك في الإطار</Label>
              <div className="mt-2 relative bg-white border-2 border-border rounded-sm" style={{ height: 220 }}>
                <canvas
                  ref={canvasRef}
                  onMouseDown={start}
                  onMouseMove={move}
                  onMouseUp={stop}
                  onMouseLeave={stop}
                  onTouchStart={start}
                  onTouchMove={move}
                  onTouchEnd={stop}
                  className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
                  data-testid={`${testid}-canvas`}
                />
                {!hasInk && (
                  <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm pointer-events-none">
                    ارسم هنا...
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={clear}
                data-testid={`${testid}-clear`}
                className="mt-2 text-sm text-muted-foreground hover:text-destructive flex items-center gap-1"
              >
                <Eraser className="w-3.5 h-3.5" /> مسح
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              <X className="w-4 h-4 ms-1" /> إلغاء
            </Button>
            <Button onClick={save} disabled={saving} data-testid={`${testid}-save`} className="bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 ms-1 animate-spin" /> : <Check className="w-4 h-4 ms-1" />}
              حفظ التوقيع
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
